import React, { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Trash2, Save, X, Pencil, AlertTriangle, MapPin } from "lucide-react";

const SEVERITIES = [
  { value: "closure", label: "Vollsperrung", color: "#DC2626" },
  { value: "warning", label: "Warnung / Engstelle", color: "#F59E0B" },
  { value: "detour", label: "Umleitung", color: "#2563EB" },
];

// Default coords near Quartier am Hornschen Tor
const DEFAULT_LAT = 51.9302;
const DEFAULT_LNG = 8.8852;

export default function AdminSperrungen() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);

  async function load() {
    setLoading(true);
    try {
      const { data } = await api.get("/sperrungen");
      setItems(data || []);
    } finally { setLoading(false); }
  }
  useEffect(() => { load(); }, []);

  async function save(s) {
    if (!s.title?.trim()) { toast.error("Titel ist erforderlich"); return; }
    const lat = parseFloat(s.lat); const lng = parseFloat(s.lng);
    if (!Number.isFinite(lat) || !Number.isFinite(lng)) { toast.error("Koordinaten erforderlich (lat/lng)"); return; }
    const payload = {
      title: s.title.trim(),
      description: s.description || "",
      lat, lng,
      start_date: s.start_date || "",
      end_date: s.end_date || "",
      severity: s.severity || "closure",
      active: s.active !== false,
    };
    try {
      if (s.__new) {
        await api.post("/sperrungen", payload);
        toast.success("Sperrung angelegt");
      } else {
        await api.put(`/sperrungen/${s.id}`, payload);
        toast.success("Gespeichert");
      }
      setEditing(null);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  }

  async function del(id) {
    if (!window.confirm("Sperrung wirklich löschen?")) return;
    try { await api.delete(`/sperrungen/${id}`); toast.success("Gelöscht"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  }

  return (
    <div data-testid="admin-sperrungen">
      <div className="flex items-start justify-between mb-1">
        <h1 className="font-semibold text-3xl">Sperrungen</h1>
        <button
          onClick={() => setEditing({
            __new: true,
            title: "", description: "",
            lat: DEFAULT_LAT, lng: DEFAULT_LNG,
            start_date: "", end_date: "",
            severity: "closure", active: true,
          })}
          data-testid="sperrung-new"
          className="inline-flex items-center gap-2 bg-[hsl(var(--brand-green-dark))] text-white px-5 py-2.5 text-sm hover:bg-[hsl(var(--brand-green-strong))]"
        >
          <Plus className="h-4 w-4" /> Neue Sperrung
        </button>
      </div>
      <p className="text-sm text-neutral-500 mb-8">Straßensperrungen, Umleitungen und Warnungen pflegen — werden auf der LIVE-Verkehr-Karte angezeigt.</p>

      <div className="bg-white border border-brand-line">
        {loading ? (
          <p className="p-8 text-sm text-neutral-500">Lade…</p>
        ) : items.length === 0 ? (
          <p className="p-8 text-sm text-neutral-500">Noch keine Sperrungen angelegt.</p>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-neutral-50 text-xs uppercase tracking-wider text-neutral-500">
              <tr>
                <th className="text-left px-5 py-3 font-normal w-8" />
                <th className="text-left px-5 py-3 font-normal">Titel</th>
                <th className="text-left px-5 py-3 font-normal">Position</th>
                <th className="text-left px-5 py-3 font-normal">Zeitraum</th>
                <th className="text-left px-5 py-3 font-normal w-24">Status</th>
                <th className="w-32" />
              </tr>
            </thead>
            <tbody>
              {items.map((s) => {
                const sev = SEVERITIES.find((x) => x.value === s.severity) || SEVERITIES[0];
                return (
                  <tr key={s.id} className="border-t border-brand-line" data-testid={`sperrung-row-${s.id}`}>
                    <td className="px-5 py-3 align-top">
                      <span
                        className="inline-flex items-center justify-center h-6 w-6 rounded-full text-white"
                        style={{ background: sev.color }}
                        title={sev.label}
                      >
                        <AlertTriangle className="h-3.5 w-3.5" />
                      </span>
                    </td>
                    <td className="px-5 py-3 align-top">
                      <p className="font-medium text-neutral-900">{s.title}</p>
                      <p className="text-xs text-neutral-500">{sev.label}</p>
                    </td>
                    <td className="px-5 py-3 align-top text-xs text-neutral-600">
                      <span className="inline-flex items-center gap-1.5">
                        <MapPin className="h-3 w-3" />
                        {Number(s.lat).toFixed(5)} · {Number(s.lng).toFixed(5)}
                      </span>
                    </td>
                    <td className="px-5 py-3 align-top text-xs text-neutral-600">
                      {(s.start_date || s.end_date)
                        ? `${s.start_date || "–"} → ${s.end_date || "–"}`
                        : <span className="text-neutral-400">—</span>}
                    </td>
                    <td className="px-5 py-3 align-top">
                      <span className={`text-xs ${s.active ? "text-[hsl(var(--brand-green-strong))]" : "text-neutral-400"}`}>
                        {s.active ? "Aktiv" : "Inaktiv"}
                      </span>
                    </td>
                    <td className="px-5 py-3 align-top text-right">
                      <div className="inline-flex items-center gap-2">
                        <button onClick={() => setEditing(s)} data-testid={`edit-sperrung-${s.id}`} className="text-neutral-500 hover:text-[hsl(var(--brand-green-strong))]"><Pencil className="h-4 w-4" /></button>
                        <button onClick={() => del(s.id)} data-testid={`delete-sperrung-${s.id}`} className="text-neutral-500 hover:text-red-600"><Trash2 className="h-4 w-4" /></button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {editing && <SperrungEditor sperrung={editing} onClose={() => setEditing(null)} onSave={save} />}
    </div>
  );
}

function SperrungEditor({ sperrung, onClose, onSave }) {
  const [s, setS] = useState({ ...sperrung });

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-6" onClick={onClose}>
      <div className="bg-white max-w-2xl w-full p-8 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="sperrung-editor">
        <div className="flex items-start justify-between mb-6">
          <h2 className="font-semibold text-2xl">{sperrung.__new ? "Neue Sperrung" : "Sperrung bearbeiten"}</h2>
          <button onClick={onClose} className="text-neutral-400 hover:text-neutral-900"><X className="h-5 w-5" /></button>
        </div>

        <div className="space-y-4">
          <label className="block">
            <span className="block text-xs text-neutral-500 mb-1.5">Titel *</span>
            <input
              value={s.title}
              onChange={(e) => setS({ ...s, title: e.target.value })}
              data-testid="sperrung-title"
              placeholder="z. B. Vollsperrung Hornsche Straße"
              className="w-full border border-brand-line px-3 py-2.5 text-sm focus:outline-none focus:border-[hsl(var(--brand-green-strong))]"
            />
          </label>

          <label className="block">
            <span className="block text-xs text-neutral-500 mb-1.5">Beschreibung</span>
            <textarea
              value={s.description}
              onChange={(e) => setS({ ...s, description: e.target.value })}
              data-testid="sperrung-description"
              placeholder="Details, Umleitungshinweise…"
              className="w-full border border-brand-line px-3 py-2.5 text-sm min-h-[80px] focus:outline-none focus:border-[hsl(var(--brand-green-strong))]"
            />
          </label>

          <div className="grid sm:grid-cols-2 gap-4">
            <label className="block">
              <span className="block text-xs text-neutral-500 mb-1.5">Breitengrad (lat) *</span>
              <input
                type="number" step="0.000001"
                value={s.lat}
                onChange={(e) => setS({ ...s, lat: e.target.value })}
                data-testid="sperrung-lat"
                className="w-full border border-brand-line px-3 py-2.5 text-sm font-mono focus:outline-none focus:border-[hsl(var(--brand-green-strong))]"
              />
            </label>
            <label className="block">
              <span className="block text-xs text-neutral-500 mb-1.5">Längengrad (lng) *</span>
              <input
                type="number" step="0.000001"
                value={s.lng}
                onChange={(e) => setS({ ...s, lng: e.target.value })}
                data-testid="sperrung-lng"
                className="w-full border border-brand-line px-3 py-2.5 text-sm font-mono focus:outline-none focus:border-[hsl(var(--brand-green-strong))]"
              />
            </label>
          </div>
          <p className="text-xs text-neutral-400 -mt-2">
            Tipp: Koordinaten ermitteln über <a href="https://www.openstreetmap.org/?mlat=51.9302&mlon=8.8852#map=18/51.9302/8.8852" target="_blank" rel="noreferrer" className="text-[hsl(var(--brand-green-strong))] hover:underline">openstreetmap.org</a> – Rechtsklick auf die Straße → „Adresse anzeigen".
          </p>

          <div className="grid sm:grid-cols-3 gap-4">
            <label className="block">
              <span className="block text-xs text-neutral-500 mb-1.5">Art</span>
              <select
                value={s.severity}
                onChange={(e) => setS({ ...s, severity: e.target.value })}
                data-testid="sperrung-severity"
                className="w-full border border-brand-line px-3 py-2.5 text-sm focus:outline-none"
              >
                {SEVERITIES.map((sv) => <option key={sv.value} value={sv.value}>{sv.label}</option>)}
              </select>
            </label>
            <label className="block">
              <span className="block text-xs text-neutral-500 mb-1.5">Von (optional)</span>
              <input
                type="date"
                value={s.start_date || ""}
                onChange={(e) => setS({ ...s, start_date: e.target.value })}
                className="w-full border border-brand-line px-3 py-2.5 text-sm focus:outline-none"
              />
            </label>
            <label className="block">
              <span className="block text-xs text-neutral-500 mb-1.5">Bis (optional)</span>
              <input
                type="date"
                value={s.end_date || ""}
                onChange={(e) => setS({ ...s, end_date: e.target.value })}
                className="w-full border border-brand-line px-3 py-2.5 text-sm focus:outline-none"
              />
            </label>
          </div>

          <label className="inline-flex items-center gap-2 text-sm">
            <input type="checkbox" checked={s.active !== false} onChange={(e) => setS({ ...s, active: e.target.checked })} />
            Auf der Karte anzeigen
          </label>
        </div>

        <div className="flex justify-end gap-3 mt-6">
          <button onClick={onClose} className="border border-brand-line px-5 py-2.5 text-sm hover:bg-neutral-50">Abbrechen</button>
          <button onClick={() => onSave(s)} data-testid="sperrung-save" className="inline-flex items-center gap-2 bg-[hsl(var(--brand-green-dark))] text-white px-6 py-2.5 text-sm hover:bg-[hsl(var(--brand-green-strong))]">
            <Save className="h-4 w-4" /> Speichern
          </button>
        </div>
      </div>
    </div>
  );
}
